/**
 * NGS cart polish — two small behaviours patterned on sad_boy_saga's cart.
 *
 * That store runs Liquid Ajax Cart + a "Coretex" dialog framework, so none of its
 * CODE transfers to Horizon. These are the two ideas worth stealing, reimplemented
 * against this theme's own machinery.
 *
 * 1. PRESERVE <details> OPEN STATE ACROSS CART RE-RENDERS.
 *    Every cart mutation re-renders a section. Any accordion the shopper opened —
 *    the discount-code row, order notes — snaps shut underneath them, because the
 *    server always renders it closed. sad_boy_saga keeps a Map of states and
 *    restores it after each render; same idea here.
 *
 *    Hooked to a MutationObserver rather than a cart event on purpose: this theme
 *    has several repaint paths (its own hydration morph, a full morph on foreign
 *    events, a Section-Rendering refetch, and spk-mystery-gate morphing directly).
 *    Observing the DOM catches all of them without having to enumerate them.
 *
 * 2. FLAG THE BODY FOR 4s AFTER AN ADD.
 *    `body[cart-state="done"]` gives CSS a hook to acknowledge the add. Detecting
 *    an add is the subtle part: CartAddEvent.eventName IS ThemeEvents.cartUpdate,
 *    so every cart change arrives on one event name. The add response is the
 *    discriminator — /cart/add.js returns only the ADDED lines, so it carries
 *    `items` but no `item_count`. A real cart always has both.
 */

import { ThemeEvents } from '@theme/events';

/* -------------------------------------------------------------------------- */
/* 1. details state                                                           */
/* -------------------------------------------------------------------------- */

const CART_SCOPE = 'cart-items-component, .cart-drawer__dialog, .cart-page, #MainContent';
const openState = new Map();

/** Stable identity for an accordion across re-renders. */
function keyFor(el) {
  const explicit = el.getAttribute('data-spk-details');
  if (explicit) return explicit;
  const summary = el.querySelector('summary');
  const label = summary?.textContent?.trim();
  return label ? `label:${label}` : '';
}

function inCart(el) {
  return !!el.closest(CART_SCOPE);
}

// `toggle` does not bubble, so capture is required.
document.addEventListener(
  'toggle',
  (event) => {
    const el = event.target;
    if (!(el instanceof HTMLDetailsElement) || !inCart(el)) return;
    const key = keyFor(el);
    if (key) openState.set(key, el.open);
  },
  true
);

function restoreDetails() {
  if (!openState.size) return;
  for (const el of document.querySelectorAll('details')) {
    if (!inCart(el)) continue;
    const key = keyFor(el);
    if (!key || !openState.has(key)) continue;
    const want = openState.get(key);
    // Only write when it actually differs — assigning `open` fires another
    // toggle, and writing unconditionally would loop through the observer.
    if (el.open !== want) el.open = want;
  }
}

let restoreQueued = false;
function queueRestore() {
  if (restoreQueued) return;
  restoreQueued = true;
  requestAnimationFrame(() => {
    restoreQueued = false;
    restoreDetails();
  });
}

const observer = new MutationObserver((records) => {
  for (const r of records) {
    if (r.type === 'childList' && (r.addedNodes.length || r.removedNodes.length)) {
      queueRestore();
      return;
    }
  }
});

function observeCart() {
  for (const root of document.querySelectorAll('cart-items-component, .cart-drawer__dialog')) {
    observer.observe(root, { childList: true, subtree: true });
  }
}

/* -------------------------------------------------------------------------- */
/* 2. post-add body flag                                                      */
/* -------------------------------------------------------------------------- */

const DONE_MS = 4000;
let doneTimer = null;

/**
 * /cart/add.js returns ONLY the added lines: `items` present, `item_count`
 * absent. A full cart always carries `item_count` and `total_price`.
 */
function looksLikeAddResponse(resource) {
  return (
    !!resource &&
    Array.isArray(resource.items) &&
    typeof resource.item_count !== 'number' &&
    typeof resource.total_price !== 'number'
  );
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (!looksLikeAddResponse(event?.detail?.resource)) return;
  document.body.setAttribute('cart-state', 'done');
  clearTimeout(doneTimer);
  doneTimer = setTimeout(() => document.body.removeAttribute('cart-state'), DONE_MS);
});

/* -------------------------------------------------------------------------- */

function init() {
  observeCart();
  restoreDetails();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

// A BFCache restore replays the heap; re-attach to whatever DOM came back.
window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    observer.disconnect();
    observeCart();
  }
});
